import {MaturityOrder, OrderStatusEnum, PriorityTypeEnum, MuniOrderType, RetailTypeEnum, QuantityCellValidationState} from './deal-series.types';
import { Condition } from '../cell-renderers/deal-conditions';
import { logger } from '../../../utils';
import { isRepriceWindow } from './deal-status';
import { MINIMUM_REQUIRED_WHEN_PAR_NOT_DIVISIBLE_BY_5_MESSAGE } from './deal-series.constants';

/** PENDING / PENDING_CANCELLED — waiting for API response. */
export const isOrderInTransit = (order: MaturityOrder | null | undefined): boolean =>
  order?.ORDER_STATUS === OrderStatusEnum.PENDING ||
  order?.ORDER_STATUS === OrderStatusEnum.PENDING_CANCELLED;

// Formats numeric values with thousands separators for display. Order amounts are always integers.
export const orderAmountFormatter = (input: string | number): string => {
  if (input === null || input === undefined || input === '') {
    return '';
  }
  const num = typeof input === 'string' ? parseInt(input.replace(/,/g, ''), 10) : Math.floor(Number(input));
  if (isNaN(num)) {
    return '';
  }
  return num.toLocaleString('en-US');
};

/** Principal amount hover modal: $ prefix, thousands separators, two decimal places. */
export const formatPrincipalAmountForModal = (input: string | number | null | undefined): string => {
  if (input === null || input === undefined || input === '') {
    return '';
  }
  const num = typeof input === 'string' ? parseFloat(input.replace(/,/g, '')) : Number(input);
  if (isNaN(num)) {
    return '';
  }
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const isZeroCouponMaturity = (securityType?: string | null): boolean =>
  securityType?.trim().toUpperCase() === 'ZERO';

/** Order amount cap: principal for zero coupons, par otherwise. */
export const getOrderAmountCap = (order: MaturityOrder | null | undefined): string => {
  if (!order) return '';
  if (isZeroCouponMaturity(order.SECURITY_TYPE)) {
    const principal = String(order.MATURITY_PRESENT_VALUE ?? '').trim();
    return principal !== '' ? principal : String(order.MATURITY_AMOUNT ?? '').trim();
  }
  return String(order.MATURITY_AMOUNT ?? '').trim();
};

export const hasZeroCouponPrincipalAmount = (order: MaturityOrder | null | undefined): boolean => {
  if (!isZeroCouponMaturity(order?.SECURITY_TYPE)) return false;
  const principal = parseNumberAmount(String(order?.MATURITY_PRESENT_VALUE ?? ''));
  return principal !== null && principal > 0;
};

/** Price-change flag that drives order-cap reconfirm/auto-reset for this maturity type. */
export const orderAmountChangeFlagActive = (order: MaturityOrder | null | undefined): boolean => {
  if (!order) return false;
  if (isZeroCouponMaturity(order.SECURITY_TYPE)) {
    return order.MATURITY_PRESENT_VALUE_CHANGED === true;
  }
  return order.MATURITY_AMOUNT_CHANGED === true;
};

/** Amber highlight on principal hover: reprice window and principal amount changed only. */
export const shouldHighlightPrincipalAmountOnHover = (
  order: MaturityOrder | null | undefined,
  dealStatus: string | null | undefined,
): boolean =>
  isRepriceWindow(dealStatus) && order?.MATURITY_PRESENT_VALUE_CHANGED === true;

/**
 * All-active rows normally display current cap (par or principal). When QUANTITY still differs from
 * cap (e.g. after cap increase and reset to last submitted snapshot), show the actual quantity.
 */
const shouldDisplayCapForAutoResetRow = (order: MaturityOrder): boolean => {
  const cap = parseNumberAmount(getOrderAmountCap(order));
  if (cap === null || cap <= 0) return false;
  const qtyStr = String(order.QUANTITY ?? '').trim();
  if (qtyStr === '') return true;
  const qty = parseNumberAmount(qtyStr);
  return qty !== null && qty === cap;
};

/**
 * Display value for the order-amount cell. All-active rows show cap, not QUANTITY.
 * Zero quantity displays as empty (not a valid order amount).
 */
export const formatOrderQuantityForCell = (
  order: MaturityOrder | null | undefined,
): string => {
  if (!order) return '';
  if (order.IS_AUTO_RESET_ORDER_AMOUNT && shouldDisplayCapForAutoResetRow(order)) {
    const cap = getOrderAmountCap(order);
    return cap === '' ? '' : orderAmountFormatter(cap);
  }
  const qty = String(order.QUANTITY ?? '').trim();
  if (qty === '') {
    return order.IS_ZERO_CANCEL_INTENT === true ? '0' : '';
  }
  const num = parseFloat(qty.replace(/,/g, ''));
  if (!isNaN(num) && num === 0) {
    if (canSetZeroCancelOrderAmount(order)) return '0';
    return '';
  }
  return orderAmountFormatter(order.QUANTITY);
};

/** Whether the user may enter quantity 0 to mark a zero-cancel (submitted-track statuses). */
export const canSetZeroCancelOrderAmount = (order: MaturityOrder | null | undefined): boolean => {
  if (!order?.ORDER_STATUS) return false;
  if (order.IS_ZERO_CANCEL_INTENT) return true;
  return SUBMITTED_TRACK_EDIT_STATUSES.has(order.ORDER_STATUS);
};

/**
 * Skip persisting order amount when the maturity has no order status yet and the user enters zero.
 * Avoids creating a DRAFT on the backend for a no-op zero entry on an empty row.
 */
export const shouldIgnoreOrderAmountSaveWhenNoStatusAndZero = (
  order: MaturityOrder | null | undefined,
  parsedAmount: number | null,
): boolean => !order?.ORDER_STATUS && parsedAmount === 0;

const SUBMITTED_TRACK_EDIT_STATUSES: ReadonlySet<OrderStatusEnum> = new Set([
  OrderStatusEnum.SUBMITTED,
  OrderStatusEnum.ACCEPTED,
  OrderStatusEnum.SUBMITTED_AND_DRAFT,
]);

/**
 * Patch after row/series All is turned off. Drafts → 0; submitted-track → empty + status flip to
 * SUBMITTED_AND_DRAFT so the grid immediately mirrors the backend transition (save with empty
 * quantity always flips SUBMITTED/ACCEPTED → SUBMITTED_AND_DRAFT). Bulk save uses isClearingAllAmounts.
 */
export const getOrderPatchAfterAllClear = (
  order: MaturityOrder,
): Pick<MaturityOrder, 'QUANTITY' | 'IS_AUTO_RESET_ORDER_AMOUNT' | 'IS_ZERO_CANCEL_INTENT' | 'ORDER_STATUS'> => {
  if (order.ORDER_STATUS === OrderStatusEnum.DRAFT) {
    return { IS_AUTO_RESET_ORDER_AMOUNT: false, QUANTITY: '0', IS_ZERO_CANCEL_INTENT: false };
  }
  if (
    order.ORDER_STATUS === OrderStatusEnum.SUBMITTED ||
    order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT ||
    order.ORDER_STATUS === OrderStatusEnum.ACCEPTED
  ) {
    return {
      IS_AUTO_RESET_ORDER_AMOUNT: false,
      IS_ZERO_CANCEL_INTENT: false,
      QUANTITY: '',
      ORDER_STATUS: OrderStatusEnum.SUBMITTED_AND_DRAFT,
    };
  }
  return { IS_AUTO_RESET_ORDER_AMOUNT: false, QUANTITY: '0', IS_ZERO_CANCEL_INTENT: false };
};

/** Baseline quantity for submitted-track edit comparisons (SUBMITTED_QUANTITY, else persisted QUANTITY). */
export const getSubmittedTrackBaselineQuantity = (order: MaturityOrder): string => {
  const submittedQty = String(order.SUBMITTED_QUANTITY ?? '').trim();
  if (submittedQty !== '') {
    return allowOnlyWholePositiveNumbers(
      normalizeNumericValueForStorage(submittedQty, order) ?? submittedQty,
    );
  }
  if (
    order.ORDER_STATUS === OrderStatusEnum.SUBMITTED ||
    order.ORDER_STATUS === OrderStatusEnum.ACCEPTED
  ) {
    const quantity = String(order.QUANTITY ?? '').trim();
    return quantity === ''
      ? ''
      : allowOnlyWholePositiveNumbers(
          normalizeNumericValueForStorage(quantity, order) ?? quantity,
        );
  }
  return '';
};

const ACCEPTED_BASELINE_TRACK_FIELDS: (keyof MaturityOrder)[] = [
  'SUBMITTED_QUANTITY',
  'IS_CONFIRMED',
  'RECONFIRM_ELIGIBLE',
  'ERROR_MESSAGE',
  'IS_AUTO_RESET_ORDER_AMOUNT',
];

/** Immutable submitted-track snapshot used to detect local edits and offer reset (grid + split modal). */
export function buildAcceptedOrderBaseline(
  order: MaturityOrder,
  splitAmount?: string,
): MaturityOrder | null {
  if (splitAmount !== undefined) {
    const quantity = normalizeNumericValueForStorage(splitAmount, order) ?? '';
    const submittedQty = String(order.SUBMITTED_QUANTITY ?? '').trim();
    const orderStatus =
      order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT && submittedQty !== ''
        ? OrderStatusEnum.SUBMITTED
        : order.ORDER_STATUS ?? (submittedQty !== '' ? OrderStatusEnum.SUBMITTED : undefined);
    return { ...order, QUANTITY: quantity, ORDER_STATUS: orderStatus };
  }

  const onSubmittedTrack =
    order.ORDER_STATUS === OrderStatusEnum.SUBMITTED ||
    order.ORDER_STATUS === OrderStatusEnum.ACCEPTED ||
    order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT;
  if (!onSubmittedTrack && !hasSubmittedAmountOnRecord(order)) {
    return null;
  }

  const baselineQty = getSubmittedTrackBaselineQuantity(order);
  const quantity =
    baselineQty !== ''
      ? baselineQty
      : normalizeNumericValueForStorage(String(order.QUANTITY ?? '').trim(), order) ?? '';
  if (quantity === '' && !hasSubmittedAmountOnRecord(order)) {
    return null;
  }

  const submittedQty = String(order.SUBMITTED_QUANTITY ?? '').trim();
  const orderStatus =
    order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT && submittedQty !== ''
      ? OrderStatusEnum.SUBMITTED
      : order.ORDER_STATUS;

  return { ...order, QUANTITY: quantity, ORDER_STATUS: orderStatus };
}

/** True when a grid or split line diverges from its accepted submitted-track snapshot. */
export function orderHasEditsFromAcceptedBaseline(order: MaturityOrder): boolean {
  const baseline = buildAcceptedOrderBaseline(order);
  if (!baseline) {
    return false;
  }

  const currentQty =
    normalizeNumericValueForStorage(
      normalizeNumericValueForDisplay(String(order.QUANTITY ?? '').trim(), order) ?? '',
      order,
    ) ?? '';
  const baselineQty =
    normalizeNumericValueForStorage(
      normalizeNumericValueForDisplay(baseline.QUANTITY, baseline) ?? '',
      baseline,
    ) ?? '';
  if (currentQty !== baselineQty) {
    return true;
  }
  if (haveOrderConditionValuesChanged(baseline, order)) {
    return true;
  }
  if (order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT) {
    return true;
  }
  for (const field of ACCEPTED_BASELINE_TRACK_FIELDS) {
    if (baseline[field] !== order[field]) {
      return true;
    }
  }
  return false;
}

/** Optimistic status for split modal local edits before save (mirrors backend on manual change). */
export function getOrderStatusAfterSubmittedTrackManualEdit(
  order: MaturityOrder,
  nextQuantity: string,
): OrderStatusEnum | undefined {
  const status = order.ORDER_STATUS;
  if (status == null || !SUBMITTED_TRACK_EDIT_STATUSES.has(status)) {
    return status;
  }

  const nextQty = allowOnlyWholePositiveNumbers(String(nextQuantity ?? '').trim());
  if (nextQty === '') {
    return OrderStatusEnum.SUBMITTED_AND_DRAFT;
  }

  const baseline = getSubmittedTrackBaselineQuantity(order);
  if (baseline !== '' && nextQty !== baseline) {
    return OrderStatusEnum.SUBMITTED_AND_DRAFT;
  }
  if (status === OrderStatusEnum.SUBMITTED_AND_DRAFT && baseline !== '' && nextQty === baseline) {
    return OrderStatusEnum.SUBMITTED;
  }
  return status;
}

/** Optimistic status when split-line conditions change on a submitted-track order. */
export function getOrderStatusAfterSubmittedTrackConditionsEdit(
  previousOrder: MaturityOrder,
  updatedOrder: MaturityOrder,
): OrderStatusEnum | undefined {
  const status = previousOrder.ORDER_STATUS;
  if (status == null || !SUBMITTED_TRACK_EDIT_STATUSES.has(status)) {
    return updatedOrder.ORDER_STATUS ?? status;
  }
  if (haveOrderConditionValuesChanged(previousOrder, updatedOrder)) {
    return OrderStatusEnum.SUBMITTED_AND_DRAFT;
  }
  return updatedOrder.ORDER_STATUS ?? status;
}

/**
 * True when the row has any persisted submitted quantity on record, regardless of current status.
 * Used by global All clear to identify rows that must be visually reverted to their submitted
 * state instead of being cleared — the user has indicated this should preserve full and partial
 * submissions across the apply/clear toggle.
 */
export const hasSubmittedAmountOnRecord = (order: MaturityOrder | null | undefined): boolean => {
  if (!order) return false;
  const submittedQty = parseNumberAmount(String(order.SUBMITTED_QUANTITY ?? ''));
  return submittedQty !== null && submittedQty > 0;
};

/**
 * True when the persisted submitted portion already covers the full maturity par.
 * Status-agnostic across the submitted track (SUBMITTED, SUBMITTED_AND_DRAFT, ACCEPTED) so global
 * All apply skips these rows uniformly — there is nothing left to fill, and a no-op save would
 * otherwise flip status to SUBMITTED_AND_DRAFT on the backend.
 */
export const isSubmittedFullParCapOnRecord = (order: MaturityOrder | null | undefined): boolean => {
  if (!order) return false;
  const status = order.ORDER_STATUS;
  if (
    status !== OrderStatusEnum.SUBMITTED &&
    status !== OrderStatusEnum.SUBMITTED_AND_DRAFT &&
    status !== OrderStatusEnum.ACCEPTED
  ) {
    return false;
  }
  const cap = parseNumberAmount(getOrderAmountCap(order));
  if (cap === null || cap <= 0) return false;
  const submitted = parseNumberAmount(String(order.SUBMITTED_QUANTITY ?? ''));
  if (submitted !== null && submitted >= cap) return true;
  // Fallback for SUBMITTED rows before SUBMITTED_QUANTITY is populated (e.g. on first load).
  return status === OrderStatusEnum.SUBMITTED && isFullParOrderAmountApplied(order);
};

/**
 * True when persisted SUBMITTED_QUANTITY is set and is strictly below maturity par.
 * In Verbal Award/CXL Window, Global All excludes these rows because users cannot increase
 * above their submitted cap — use per-row All instead.
 */
export const isSubmittedQuantityBelowPar = (order: MaturityOrder | null | undefined): boolean => {
  if (!order) return false;
  const cap = parseNumberAmount(getOrderAmountCap(order));
  if (cap === null || cap <= 0) return false;
  const submitted = parseNumberAmount(String(order.SUBMITTED_QUANTITY ?? ''));
  if (submitted === null || submitted <= 0) return false;
  return submitted < cap;
};

/**
 * Full-par submitted on record but not currently shown at par (e.g. after per-row All clear).
 * These rows need a visual restore to SUBMITTED full par, not a backend fill/clear.
 */
export const needsSubmittedFullParVisualRestore = (order: MaturityOrder | null | undefined): boolean =>
  isSubmittedFullParCapOnRecord(order) && !isFullParOrderAmountApplied(order);

/** Expected grid shape after EVENT_RESET_ORDERS_LIST (also used for optimistic UI in tests). */
export const getSubmittedAmountRevertPatch = (
  order: MaturityOrder,
): Pick<MaturityOrder, 'ORDER_STATUS' | 'QUANTITY' | 'IS_AUTO_RESET_ORDER_AMOUNT' | 'IS_ZERO_CANCEL_INTENT'> => {
  const fullParOnRecord = isSubmittedFullParCapOnRecord(order);
  return {
    ORDER_STATUS: OrderStatusEnum.SUBMITTED,
    QUANTITY: fullParOnRecord
      ? getOrderAmountCap(order)
      : String(order.SUBMITTED_QUANTITY ?? '').trim(),
    IS_AUTO_RESET_ORDER_AMOUNT: fullParOnRecord,
    IS_ZERO_CANCEL_INTENT: false,
  };
};

/**
 * Whether header/series All should offer clear. Includes rows with a persisted submitted amount
 * so the toggle is meaningful even when only submitted-track rows are present (clear will visually
 * revert them; nothing is cleared on the backend).
 */
export const hasClearableAllAmountState = (orders: MaturityOrder[]): boolean =>
  orders.some(
    (o) =>
      !!o.IS_AUTO_RESET_ORDER_AMOUNT ||
      !!o.IS_ZERO_CANCEL_INTENT ||
      hasOrderAmountApplied(o) ||
      hasSubmittedAmountOnRecord(o),
  );

export const isSplitMaturityParentRow = (order: MaturityOrder): boolean =>
  order.IS_SPLIT_MATURITY === true || order.HAS_SPLIT === true;

/**
 * Split maturities filled via Global All: each child is set to full par, so the parent rollup
 * sum is a positive multiple of par (may exceed par when there are multiple children).
 */
export const isSplitGlobalAllApplied = (order: MaturityOrder): boolean => {
  if (!isSplitMaturityParentRow(order)) return false;
  const cap = parseNumberAmount(getOrderAmountCap(order));
  const qty = parseNumberAmount(String(order.QUANTITY ?? ''));
  if (cap === null || cap <= 0 || qty === null || qty <= 0) return false;
  return qty >= cap && qty % cap === 0;
};

/** Global All header is active when every maturity row is at full par (or has no par amount). */
export const isGlobalAllHeaderActive = (orders: MaturityOrder[]): boolean => {
  const rows = orders.filter((o) => o?.MATURITY_ID);
  if (!rows.length) return false;
  return rows.every((o) => {
    const cap = parseNumberAmount(getOrderAmountCap(o));
    if (cap === null || cap <= 0) return true;
    return isSplitGlobalAllApplied(o) || isFullParOrderAmountApplied(o);
  });
};

/**
 * Toggle off (clear) when every non-transit row is at full par and at least one row is clearable.
 * Otherwise the click should fill modifiable rows to par.
 */
export const shouldGlobalAllClear = (orders: MaturityOrder[]): boolean => {
  const rows = orders.filter(
    (o) =>
      o?.MATURITY_ID &&
      o.ORDER_STATUS !== OrderStatusEnum.PENDING &&
      o.ORDER_STATUS !== OrderStatusEnum.PENDING_CANCELLED,
  );
  return rows.length > 0 && isGlobalAllHeaderActive(rows) && hasClearableAllAmountState(rows);
};

/**
 * True when a row has a real applied order amount (header All: apply vs clear on first click).
 * Server/default QUANTITY of 0 does not count — only IS_AUTO_RESET or quantity > 0.
 */
export const hasOrderAmountApplied = (order: MaturityOrder | null | undefined): boolean => {
  if (!order) return false;
  if (order.IS_AUTO_RESET_ORDER_AMOUNT) return true;
  const quantityStr = String(order.QUANTITY ?? '').trim();
  if (quantityStr === '') return false;
  const quantityNum = parseFloat(quantityStr.replace(/,/g, ''));
  return !isNaN(quantityNum) && quantityNum > 0;
};

/** True when order amount is full maturity par (per-row All button active styling). */
export const isFullParOrderAmountApplied = (order: MaturityOrder | null | undefined): boolean => {
  if (!order) return false;
  const capNum = parseNumberAmount(getOrderAmountCap(order));
  if (capNum === null || capNum <= 0) return false;
  if (order.IS_AUTO_RESET_ORDER_AMOUNT) {
    return shouldDisplayCapForAutoResetRow(order);
  }
  const quantityNum = parseNumberAmount(String(order.QUANTITY ?? ''));
  if (quantityNum === null) return false;
  return quantityNum === capNum;
};

export const seriesSizeFormatter = (seriesCode: string, seriesSize: string | number): string => {
  const rawValue = seriesSize ?? '';
  const num = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue));

  if (!num || isNaN(num)) {
    return seriesCode ?? '';
  }
  
  const formattedSize = orderAmountFormatter(num);
  return `${seriesCode ?? ''} $${formattedSize}`;
};


export const formatNotesWithOverrides = (order: MaturityOrder): string => {
  const notes = [];

  if (isZeroCouponMaturity(order.SECURITY_TYPE)) {
    notes.push('CAB');
  }

  if (order.MATURITY_OVERRIDE_FITCH) {
    notes.push(`Fitch: ${order.MATURITY_OVERRIDE_FITCH}`);
  }
  if (order.MATURITY_OVERRIDE_MOODYS) {
    notes.push(`Moody's: ${order.MATURITY_OVERRIDE_MOODYS}`);
  }
  if (order.MATURITY_OVERRIDE_SANDP) {
    notes.push(`S&P: ${order.MATURITY_OVERRIDE_SANDP}`);
  }
  if (order.MATURITY_OVERRIDE_INSURANCE) {
    notes.push(`Insurance: ${order.MATURITY_OVERRIDE_INSURANCE}`);
  }
  if (order.CALL_DATE_OVERRIDE) {
    notes.push(`Callable on ${formatCallDateOverride(order.CALL_DATE_OVERRIDE)}`);
  }
  if (isPutSecurityType(order.SECURITY_TYPE)) {
    const mandatoryTender = order.MANDATORY_TENDER?.trim();
    if (mandatoryTender) {
      notes.push(`Put Type: ${mandatoryTender}`);
    }
  }

  return notes.join('; ');
};

const formatUstTreasuryMaturityDate = (
  maturityDate: number | string | null | undefined,
): string => {
  if (maturityDate === null || maturityDate === undefined || maturityDate === '') {
    return '';
  }
  if (typeof maturityDate === 'string' && Number.isNaN(Number(maturityDate))) {
    return maturityDate.trim();
  }
  const epochMs = typeof maturityDate === 'number' ? maturityDate : Number(maturityDate);
  if (!epochMs || Number.isNaN(epochMs)) {
    return '';
  }
  const { month, year } = formatMillisecondsToMonthYear(epochMs);
  return `${month}/${year}`;
};

/** Taxable UST benchmark label: TSY: {coupon} {treasury maturity MM/YYYY} */
export const formatUstBenchmark = (
  coupon: string | number | null | undefined,
  maturityDate: number | string | null | undefined,
): string => {
  const couponPart =
    coupon !== null && coupon !== undefined && coupon !== ''
      ? formatToThreeDecimals(coupon)
      : '';
  const datePart = formatUstTreasuryMaturityDate(maturityDate);
  if (!couponPart && !datePart) {
    return '';
  }
  if (!couponPart) {
    return `TSY: ${datePart}`;
  }
  if (!datePart) {
    return `TSY: ${couponPart}`;
  }
  return `TSY: ${couponPart} ${datePart}`;
};

export const formatToThreeDecimals = (input: string | number): string => {
  const num = typeof input === 'string' ? parseFloat(input) : input;
  if (isNaN(num) || num === null || num === undefined) {
    logger.warn('Invalid number input');
    return '';
  }

  // Format to three decimal places
  return num.toFixed(3);
};

export const formatSpread = (input: string | number): string => {
  const num = typeof input === 'string' ? parseFloat(input) : input;
  if (isNaN(num) || num === null || num === undefined) {
    logger.warn('Invalid number input for spread');
    return '';
  }
  // If the value is a positive integer, display '+0' (no space)
  if (Number.isInteger(num) && num > 0) {
    return `+${num}`;
  }
  // If the value is a negative integer, display as is
  if (Number.isInteger(num) && num < 0) {
    return num.toString();
  }
  // If the value has a decimal and it is positive, display '+0.0' (no space, one decimal)
  if (!Number.isInteger(num) && num > 0) {
    return `+${num.toFixed(1)}`;
  }
  // If the value has a decimal and it is negative, display as is with only one decimal
  if (!Number.isInteger(num) && num < 0) {
    return num.toFixed(1);
  }
  // Handle default case
  return '';
};

export const parseNumberAmount = (amount: string): number => {
  if (!amount) return null;
  // Reuse allowOnlyWholePositiveNumbers to strip everything except digits (removes decimals, commas, etc.)
  const cleanedAmount = allowOnlyWholePositiveNumbers(amount);
  return cleanedAmount ? parseInt(cleanedAmount, 10) : null; // Use parseInt instead of parseFloat
};

export type ManualOrderQuantityEditResult = {
  quantity: string;
  isZeroCancelIntent: boolean;
  shouldIgnore: boolean;
};

/** Shared manual order-amount commit rules for maturity grid and split modal. */
export function resolveManualOrderQuantityEdit(
  order: MaturityOrder | null | undefined,
  rawValue: string,
): ManualOrderQuantityEditResult {
  const parsed = parseNumberAmount(rawValue);
  if (shouldIgnoreOrderAmountSaveWhenNoStatusAndZero(order, parsed)) {
    return {
      quantity: String(order?.QUANTITY ?? '').trim(),
      isZeroCancelIntent: false,
      shouldIgnore: true,
    };
  }
  const canSetZeroCancel = canSetZeroCancelOrderAmount(order);
  const onSubmittedTrack =
    order?.ORDER_STATUS != null && SUBMITTED_TRACK_EDIT_STATUSES.has(order.ORDER_STATUS);
  let quantity: string;
  if (parsed === null) {
    quantity = onSubmittedTrack ? '0' : '';
  } else if (parsed === 0) {
    quantity = canSetZeroCancel ? '0' : '';
  } else {
    quantity = String(parsed);
  }
  const isZeroCancelIntent =
    quantity === '0' &&
    canSetZeroCancelOrderAmount({ ...(order as MaturityOrder), QUANTITY: quantity });
  return { quantity, isZeroCancelIntent, shouldIgnore: false };
}

export const validateAmount = (amount: string): boolean => {
  // Empty string is considered valid (no amount set)
  if (!amount || amount.trim() === '') {
    return true;
  }
  const parsed = parseNumberAmount(amount);
  return parsed !== null && !Number.isNaN(parsed);
};

export interface OrderFieldValidation {
  hasError: boolean;
  message: string;
}

export type OrderAmountFieldType = 'quantity' | 'minimum' | 'maximum';

export interface OrderAmountMultiplesContext {
  maturityAmount?: string | null;
  minimumOrderAmount?: string | null;
  field?: OrderAmountFieldType;
}

const DEFAULT_NULL_MULTIPLES = 5;

/** When multiples is null and par is not divisible by 5, amounts use minimum as increment instead. */
export const isMinimumIncrementMode = (
  multiples: number | null | undefined,
  maturityAmount: string | null | undefined
): boolean => {
  if (multiples != null && multiples > 0) {
    return false;
  }
  const parNum = parseFloat(String(maturityAmount ?? ''));
  if (isNaN(parNum) || parNum <= 0) {
    return false;
  }
  return parNum % DEFAULT_NULL_MULTIPLES !== 0;
};

export const hasMinimumOrderAmountSet = (minimumOrderAmount?: string | null): boolean => {
  if (minimumOrderAmount == null || String(minimumOrderAmount).trim() === '') {
    return false;
  }
  const minNum = parseFloat(String(minimumOrderAmount));
  return !isNaN(minNum) && minNum > 0;
};

export const hasMaximumOrderAmountSet = (maximumOrderAmount?: string | null): boolean => {
  if (maximumOrderAmount == null || String(maximumOrderAmount).trim() === '') {
    return false;
  }
  const maxNum = parseFloat(String(maximumOrderAmount));
  return !isNaN(maxNum) && maxNum > 0;
};

/** Minimum is required when multiples is null, par is not divisible by 5, and the order has a quantity. */
export const validateMinimumRequiredForMinimumIncrementMode = (
  multiples: number | null | undefined,
  maturityAmount: string | null | undefined,
  minimumOrderAmount?: string | null,
  quantity?: string | null
): OrderFieldValidation => {
  if (!isMinimumIncrementMode(multiples, maturityAmount)) {
    return { hasError: false, message: '' };
  }
  const qtyNum = quantity ? parseFloat(String(quantity)) : 0;
  if (isNaN(qtyNum) || qtyNum <= 0) {
    return { hasError: false, message: '' };
  }
  if (hasMinimumOrderAmountSet(minimumOrderAmount)) {
    return { hasError: false, message: '' };
  }
  return { hasError: true, message: MINIMUM_REQUIRED_WHEN_PAR_NOT_DIVISIBLE_BY_5_MESSAGE };
};

export const PAR_AMOUNT_EXCEEDED_MESSAGE = 'Cannot exceed Par Amount ($M)';
export const SPLIT_MATURITY_PAR_EXCEEDED_TOOLTIP = 'Maturity Par Amount ($M) exceeded';
export const PRINCIPAL_AMOUNT_EXCEEDED_MESSAGE = 'Order Amount exceeds Principal Amount';
export const MAXIMUM_PRINCIPAL_AMOUNT_EXCEEDED_MESSAGE = 'Cannot exceed Principal Amount';
export const CANNOT_EXCEED_MAXIMUM_CONDITION_MESSAGE = 'Cannot exceed Maximum Condition';
export const CANNOT_EXCEED_MINIMUM_CONDITION_MESSAGE = 'Cannot be less than Minimum Condition';

export const getOrderAmountCapExceededMessage = (
  securityType?: string | null,
  field: OrderAmountFieldType = 'quantity',
): string => {
  if (isZeroCouponMaturity(securityType)) {
    return field === 'maximum'
      ? MAXIMUM_PRINCIPAL_AMOUNT_EXCEEDED_MESSAGE
      : PRINCIPAL_AMOUNT_EXCEEDED_MESSAGE;
  }
  return PAR_AMOUNT_EXCEEDED_MESSAGE;
};

export const getOrderAmountCapExceededTooltip = (
  order: MaturityOrder,
  amount: string | number | null | undefined,
  cap: string | null | undefined,
): string => {
  const capLabel = isZeroCouponMaturity(order.SECURITY_TYPE) ? 'Principal Amount' : 'Par Amount ($M)';
  return `Order amount (${orderAmountFormatter(amount)}) exceeds ${capLabel} (${orderAmountFormatter(cap)})`;
};

export const validateAmountAgainstParAmount = (
  amount: string | number | null | undefined,
  maturityAmount: string | null | undefined,
  securityType?: string | null,
  field: OrderAmountFieldType = 'quantity',
): OrderFieldValidation => {
  if (!maturityAmount) {
    return { hasError: false, message: '' };
  }

  const valueStr = String(amount ?? '').trim();
  const valueNum = valueStr === '' ? 0 : parseFloat(valueStr);

  if (valueStr === '' || valueNum === 0 || isNaN(valueNum)) {
    return { hasError: false, message: '' };
  }

  if (!hasValidOrderQuantity(valueStr, maturityAmount)) {
    return { hasError: true, message: getOrderAmountCapExceededMessage(securityType, field) };
  }

  return { hasError: false, message: '' };
};

export const validateMinimumAgainstParAmount = (
  minimum: string | number | null | undefined,
  maturityAmount: string | null | undefined,
): OrderFieldValidation => {
  if (!maturityAmount) {
    return { hasError: false, message: '' };
  }

  const valueStr = String(minimum ?? '').trim();
  const valueNum = valueStr === '' ? 0 : parseFloat(valueStr);

  if (valueStr === '' || valueNum === 0 || isNaN(valueNum)) {
    return { hasError: false, message: '' };
  }

  if (!hasValidOrderQuantity(valueStr, maturityAmount)) {
    return { hasError: true, message: PAR_AMOUNT_EXCEEDED_MESSAGE };
  }

  return { hasError: false, message: '' };
};

export const validateMinimumAgainstMaximum = (
  minimumOrderAmount?: string | null,
  maximumOrderAmount?: string | null,
): OrderFieldValidation => {
  if (!hasMinimumOrderAmountSet(minimumOrderAmount) || !hasMaximumOrderAmountSet(maximumOrderAmount)) {
    return { hasError: false, message: '' };
  }
  const minNum = parseFloat(String(minimumOrderAmount));
  const maxNum = parseFloat(String(maximumOrderAmount));
  if (minNum > maxNum) {
    return { hasError: true, message: CANNOT_EXCEED_MAXIMUM_CONDITION_MESSAGE };
  }
  return { hasError: false, message: '' };
};

export const validateMaximumAgainstMinimum = (
  maximumOrderAmount?: string | null,
  minimumOrderAmount?: string | null,
): OrderFieldValidation => {
  if (!hasMaximumOrderAmountSet(maximumOrderAmount) || !hasMinimumOrderAmountSet(minimumOrderAmount)) {
    return { hasError: false, message: '' };
  }
  const minNum = parseFloat(String(minimumOrderAmount));
  const maxNum = parseFloat(String(maximumOrderAmount));
  if (maxNum < minNum) {
    return { hasError: true, message: CANNOT_EXCEED_MINIMUM_CONDITION_MESSAGE };
  }
  return { hasError: false, message: '' };
};

export const getMinimumOrderAmountValidation = (
  order: MaturityOrder | null | undefined,
): OrderFieldValidation => {
  if (!order) {
    return { hasError: false, message: '' };
  }

  const requiredValidation = validateMinimumRequiredForMinimumIncrementMode(
    order.MULTIPLES,
    order.MATURITY_AMOUNT,
    order.MINIMUM_ORDER_AMOUNT,
    order.QUANTITY,
  );
  if (requiredValidation.hasError) {
    return requiredValidation;
  }
  if (!hasMinimumOrderAmountSet(order.MINIMUM_ORDER_AMOUNT)) {
    return { hasError: false, message: '' };
  }

  const parValidation = validateMinimumAgainstParAmount(
    order.MINIMUM_ORDER_AMOUNT,
    order.MATURITY_AMOUNT,
  );
  if (parValidation.hasError) {
    return parValidation;
  }

  const crossValidation = validateMinimumAgainstMaximum(
    order.MINIMUM_ORDER_AMOUNT,
    order.MAXIMUM_ORDER_AMOUNT,
  );
  if (crossValidation.hasError) {
    return crossValidation;
  }

  const multiplesValidation = validateOrderAmountForMultiples(
    order.MINIMUM_ORDER_AMOUNT,
    order.MULTIPLES,
    { maturityAmount: order.MATURITY_AMOUNT, field: 'minimum' },
  );
  return {
    hasError: !multiplesValidation.isValid,
    message: multiplesValidation.errorMessage ?? '',
  };
};

export const getMaximumOrderAmountValidation = (
  order: MaturityOrder | null | undefined,
): OrderFieldValidation => {
  if (!order) {
    return { hasError: false, message: '' };
  }
  if (!hasMaximumOrderAmountSet(order.MAXIMUM_ORDER_AMOUNT)) {
    return { hasError: false, message: '' };
  }

  const orderCap = getOrderAmountCap(order);
  const parValidation = validateAmountAgainstParAmount(
    order.MAXIMUM_ORDER_AMOUNT,
    orderCap,
    order.SECURITY_TYPE,
    'maximum',
  );
  if (parValidation.hasError) {
    return parValidation;
  }

  const crossValidation = validateMaximumAgainstMinimum(
    order.MAXIMUM_ORDER_AMOUNT,
    order.MINIMUM_ORDER_AMOUNT,
  );
  if (crossValidation.hasError) {
    return crossValidation;
  }

  const multiplesValidation = validateOrderAmountForMultiples(
    order.MAXIMUM_ORDER_AMOUNT,
    order.MULTIPLES,
    {
      maturityAmount: order.MATURITY_AMOUNT,
      minimumOrderAmount: order.MINIMUM_ORDER_AMOUNT,
      field: 'maximum',
    },
  );
  return {
    hasError: !multiplesValidation.isValid,
    message: multiplesValidation.errorMessage ?? '',
  };
};

export const getOrderAmountFieldValidation = (
  amount: string | number | null | undefined,
  capAmount: string | null | undefined,
  multiples?: number,
  validateAgainstPar = true,
  field: OrderAmountFieldType = 'quantity',
  minimumOrderAmount?: string | null,
  securityType?: string | null,
  parAmountForMultiples?: string | null,
): OrderFieldValidation => {
  if (validateAgainstPar) {
    const parValidation = validateAmountAgainstParAmount(amount, capAmount, securityType, field);
    if (parValidation.hasError) {
      return parValidation;
    }
  }

  const amountStr = amount == null ? null : String(amount);
  if (!amountStr || amountStr.trim() === '') {
    return { hasError: false, message: '' };
  }

  const multiplesValidation = validateOrderAmountForMultiples(amountStr, multiples, {
    maturityAmount: parAmountForMultiples ?? capAmount,
    minimumOrderAmount,
    field,
  });
  return {
    hasError: !multiplesValidation.isValid,
    message: multiplesValidation.errorMessage ?? '',
  };
};

/** Validates all amount fields on an order before persisting to the server. */
export const getOrderConditionsValidation = (order: MaturityOrder | null | undefined): OrderFieldValidation => {
  if (!order) {
    return { hasError: false, message: '' };
  }

  const minimumRequiredValidation = validateMinimumRequiredForMinimumIncrementMode(
    order.MULTIPLES,
    order.MATURITY_AMOUNT,
    order.MINIMUM_ORDER_AMOUNT,
    order.QUANTITY
  );
  if (minimumRequiredValidation.hasError) {
    return minimumRequiredValidation;
  }

  const orderCap = getOrderAmountCap(order);
  const quantityValidation = getOrderAmountFieldValidation(
    order.QUANTITY,
    orderCap,
    order.MULTIPLES,
    true,
    'quantity',
    order.MINIMUM_ORDER_AMOUNT,
    order.SECURITY_TYPE,
    order.MATURITY_AMOUNT,
  );
  if (quantityValidation.hasError) {
    return quantityValidation;
  }

  const minimumValidation = getMinimumOrderAmountValidation(order);
  if (minimumValidation.hasError) {
    return minimumValidation;
  }

  return getMaximumOrderAmountValidation(order);
};

export const hasValidOrderQuantity = (quantity: string, maturityAmount: string): boolean => {
  if (!quantity || quantity.trim() === '') {
    return false;
  }

  const quantityNum = parseFloat(quantity);
  const maturityAmountNum = parseFloat(maturityAmount) || 0;

  if (isNaN(quantityNum)) {
    return false;
  }

  return quantityNum > 0 && quantityNum <= maturityAmountNum;
};

export const breachesOrderConditions = (
  quantity: string | null | undefined,
  minimumAmount: string | null | undefined,
  maximumAmount: string | null | undefined
): boolean => {
  if (!quantity) {
    return false;
  }

  const quantityNum = parseFloat(String(quantity));
  if (isNaN(quantityNum) || quantityNum === 0) {
    return false;
  }

  const minNum = minimumAmount ? parseFloat(String(minimumAmount)) : 0;
  const maxNum = maximumAmount ? parseFloat(String(maximumAmount)) : 0;

  if (minNum > 0 && quantityNum < minNum) {
    return true;
  }

  if (maxNum > 0 && quantityNum > maxNum) {
    return true;
  }

  return false;
};

/**
 * Base submission amount rules: quantity vs maturity, multiples, minimum/maximum configuration.
 * Blocks submit when maximum configuration is invalid (multiples, exceeds par).
 * Quantity above maximum condition is warning-only (getQuantityCellValidationState); it does not fail here.
 * Returns true only when: quantity valid vs maturity, all amounts valid for multiples,
 * maximum does not exceed par, and quantity >= MINIMUM_ORDER_AMOUNT when set.
 */
export const isOrderPassesSubmissionAmountRules = (order: MaturityOrder | null | undefined): boolean => {
  if (!order) return false;
  if (order.IS_ZERO_CANCEL_INTENT) return false;
  const orderCap = getOrderAmountCap(order);
  if (!hasValidOrderQuantity(order.QUANTITY, orderCap)) return false;
  const { QUANTITY, MULTIPLES, MINIMUM_ORDER_AMOUNT, MAXIMUM_ORDER_AMOUNT, MATURITY_AMOUNT } = order;
  if (
    validateMinimumRequiredForMinimumIncrementMode(
      MULTIPLES,
      MATURITY_AMOUNT,
      MINIMUM_ORDER_AMOUNT,
      QUANTITY
    ).hasError
  ) {
    return false;
  }
  const multiplesContext = { maturityAmount: MATURITY_AMOUNT, minimumOrderAmount: MINIMUM_ORDER_AMOUNT };
  if (!validateOrderAmountForMultiples(QUANTITY, MULTIPLES, { ...multiplesContext, field: 'quantity' }).isValid) {
    return false;
  }
  if (getMinimumOrderAmountValidation(order).hasError) {
    return false;
  }
  if (getMaximumOrderAmountValidation(order).hasError) {
    return false;
  }
  if (breachesOrderConditions(QUANTITY, MINIMUM_ORDER_AMOUNT, undefined)) return false;
  return true;
};

export function snakeToTitleCase(text: string): string {
  if (!text) return '';

  if (!text.includes('_')) {
    return text
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  }

  return text
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export interface OptionConfig {
  value: string;
  displayValue: string;
}

const normalizeOrderTypeForApi = (orderType?: string): string => {
  const normalized = orderType?.toString()?.trim();
  if (!normalized || normalized === 'NONE') return MuniOrderType.OTHER;
  // Be defensive about a potential non-plural value coming from other environments.
  if (normalized === 'PROFESSIONAL_RETAIL') return MuniOrderType.PROFESSIONAL_RETAIL;
  return normalized;
};

export const formatOrderTypeForDisplay = (orderType?: string): string => {
  const normalized = normalizeOrderTypeForApi(orderType);
  switch (normalized) {
    case MuniOrderType.INDIVIDUAL_RETAIL:
      return 'Indiv Retail';
    case MuniOrderType.PROFESSIONAL_RETAIL:
      return 'Prof Retail';
    case MuniOrderType.OTHER:
    default:
      return 'Other';
  }
};

export const hasRetailTypeValue = (retailType?: string): boolean =>
  retailType === RetailTypeEnum.LOCAL ||
  retailType === RetailTypeEnum.STATE ||
  retailType === RetailTypeEnum.NATIONAL;

/** Retail Type is enabled only when Order Type is Indiv Retail or Prof Retail. */
export const isRetailTypeEditable = (order: MaturityOrder | null | undefined): boolean => {
  if (!order) {
    return false;
  }
  const orderType = normalizeOrderTypeForApi(order.ORDER_TYPE);
  return (
    orderType === MuniOrderType.INDIVIDUAL_RETAIL ||
    orderType === MuniOrderType.PROFESSIONAL_RETAIL
  );
};

export const applyRetailTypeConstraints = (order: MaturityOrder): MaturityOrder => {
  if (!isRetailTypeEditable(order)) {
    return { ...order, RETAIL_TYPE: RetailTypeEnum.NONE };
  }
  return order;
};

export const formatRetailTypeForDisplay = (retailType?: string): string => {
  switch (retailType) {
    case RetailTypeEnum.LOCAL:
      return 'Retail Local';
    case RetailTypeEnum.STATE:
      return 'Retail State';
    case RetailTypeEnum.NATIONAL:
      return 'Retail Nat';
    default:
      return '';
  }
};

export const formatRetailTypeForTooltip = (retailType?: string): string => {
  switch (retailType) {
    case RetailTypeEnum.LOCAL:
      return 'Local';
    case RetailTypeEnum.STATE:
      return 'State';
    case RetailTypeEnum.NATIONAL:
      return 'Nat';
    default:
      return '';
  }
};

/** Order Type pill/tooltip only for Indiv Retail and Prof Retail — not Other. */
export const shouldShowOrderTypeInSummary = (orderType?: string): boolean => {
  const normalized = normalizeOrderTypeForApi(orderType);
  return (
    normalized === MuniOrderType.INDIVIDUAL_RETAIL ||
    normalized === MuniOrderType.PROFESSIONAL_RETAIL
  );
};

export const formatPriorityTypeForDisplay = (priorityType?: string): string => {
  switch (priorityType) {
    case PriorityTypeEnum.MEMBER:
      return 'Mem';
    case PriorityTypeEnum.GROUP_NET:
      return 'GN';
    case PriorityTypeEnum.NET_DESIGNATED:
      return 'ND';
    default:
      return '';
  }
};

export const resolvePriorityTypeForSubmission = (order: MaturityOrder): string => {
  const label = formatPriorityTypeForDisplay(order.PRIORITY_TYPE);
  if (!label) {
    return PriorityTypeEnum.NONE;
  }
  return order.PRIORITY_TYPE;
};

export const resolveRetailTypeForSubmission = (order: MaturityOrder): string => {
  if (!isRetailTypeEditable(order) || !hasRetailTypeValue(order.RETAIL_TYPE)) {
    return RetailTypeEnum.NONE;
  }
  return order.RETAIL_TYPE;
};

export const mapSnakeToOptionConfig = (value: string): OptionConfig => ({
  value,
  displayValue: snakeToTitleCase(value),
});

const isTrueFlag = (value: unknown): boolean => value === true || value === 'true' || value === 'Y';

const SPLIT_SUMMARY_PARENT_ORDER = (order: MaturityOrder): MaturityOrder & {
  hasSplit?: boolean | string;
  isSplitMaturity?: boolean | string;
  splitConditionsVarious?: boolean | string;
} => order as MaturityOrder & {
  hasSplit?: boolean | string;
  isSplitMaturity?: boolean | string;
  splitConditionsVarious?: boolean | string;
};

const isSplitParentForConditionsSummary = (order: MaturityOrder): boolean => {
  const splitOrder = SPLIT_SUMMARY_PARENT_ORDER(order);
  return (
    isTrueFlag(order.IS_SPLIT_MATURITY) ||
    isTrueFlag(order.HAS_SPLIT) ||
    isTrueFlag(splitOrder.isSplitMaturity) ||
    isTrueFlag(splitOrder.hasSplit)
  );
};


/** Conditions Summary column: split-parent rollup with mixed child conditions shows Various only. */
export const getConditionsSummaryForGrid = (order: MaturityOrder): Condition[] => {
  const splitOrder = SPLIT_SUMMARY_PARENT_ORDER(order);
  const conditions: Condition[] = [];
  if (isTrueFlag(order.SPLIT_CONDITIONS_VARIOUS) || isTrueFlag(splitOrder.splitConditionsVarious)) {
    conditions.push({ text: 'Various' });
    return conditions;
  }
  conditions.push(...getConditionsSummaryConfig(order));
  return conditions;
};

export const getConditionsSummaryConfig = (order: MaturityOrder): Condition[] => {
  const conditions: Condition[] = [];
  if (order.MUNI_STRIP) {
    conditions.push({
      iconText: 'S',
      iconColor: '#3785fb',
      text: 'Strip',
      iconBackgroundColor: 'rgba(55, 133, 251, 0.2)',
    });
  }

  if (order.MUNI_AON) {
    //ToDo get aon color/icon
    conditions.push({
      iconText: 'A',
      iconColor: '#c170f6',
      text: 'AON',
      iconBackgroundColor: 'rgba(193, 112, 246, 0.2)',
    });
  }

  if (order.MINIMUM_ORDER_AMOUNT && parseFloat(order.MINIMUM_ORDER_AMOUNT) > 0) {
    //ToDo get min color/icon
    conditions.push({
      iconUrl: '/deal-icons/gauge-min.svg',
      text: 'Min',
    });
  }

  if (order.MAXIMUM_ORDER_AMOUNT && parseFloat(order.MAXIMUM_ORDER_AMOUNT) > 0) {
    conditions.push({
      iconUrl: '/deal-icons/gauge-max.svg',
      text: 'Max',
    });
  }

  // if (order.ORDER_AMOUNT_INCREAMENT) {
  //   conditions.push({
  //     iconName: 'stairs',
  //     text: 'Increment',
  //   });
  // }

  if (order.COMMENT) {
    conditions.push({
      iconName: 'comment',
      text: 'Comment',
    });
  }

  if (order.ZIP_CODE) {
    conditions.push({
      iconText: 'Z',
      iconColor: '#8a98f5',
      text: 'Zip',
      iconBackgroundColor: 'rgba(138, 152, 245, 0.2)',
    });
  }

  if (order.FLEX_MATURITY) {
    conditions.push({
      iconText: 'F',
      iconColor: '#3785fb',
      text: 'Flex Maturity',
      iconBackgroundColor: 'rgba(55, 133, 251, 0.2)',
    });
  }

  if (order.FLEX_COUPON) {
    conditions.push({
      iconText: 'C',
      iconColor: '#ff9f43',
      text: 'Flex Coupon',
      iconBackgroundColor: 'rgba(255, 159, 67, 0.2)',
    });
  }

  const priorityTypeLabel = formatPriorityTypeForDisplay(order.PRIORITY_TYPE);
  if (priorityTypeLabel) {
    conditions.push({
      iconText: 'P',
      iconColor: '#0fb666',
      text: priorityTypeLabel,
      iconBackgroundColor: 'rgba(15, 182, 102, 0.2)',
    });
  }

  if (shouldShowOrderTypeInSummary(order.ORDER_TYPE)) {
    conditions.push({
      iconText: 'R',
      iconColor: '#5ad8e0',
      text: 'Retail',
      iconBackgroundColor: 'rgba(90, 216, 224, 0.2)',
    });
  }

  return conditions;
};

export const isPutSecurityType = (securityType?: string | null): boolean =>
  securityType?.trim().toUpperCase() === 'PUT';

/** Show Put Date only when series-level SECURITY_TYPE is Put (not Various / mixed maturities). */
export const shouldShowPutDateColumn = (seriesSecurityType?: string | null): boolean =>
  isPutSecurityType(seriesSecurityType);

export const formatMillisecondsToMonthYear = (value: number): { month: string; year: number } => {
  const date = new Date(value);
  // Use UTC so the month/year displayed for a date-at-midnight epoch doesn't shift by user timezone.
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return {
    month,
    year,
  };
};

/** Same resolved label as the flex maturity field in the conditions form (series + MM/YYYY). */
export const formatFlexMaturityForDisplay = (
  flexMaturityId: string | undefined,
  flexMaturityOptions: MaturityOrder[] | undefined
): string => {
  if (!flexMaturityId || !flexMaturityOptions?.length) {
    return '';
  }
  const flexMaturity = flexMaturityOptions.find(
    (maturity) => maturity.MATURITY_ID === flexMaturityId
  );
  if (!flexMaturity) {
    return '';
  }
  const { month, year } = formatMillisecondsToMonthYear(flexMaturity.MATURITY_DATE);
  const seriesCode = flexMaturity.SERIES_CODE || '';
  return seriesCode ? `${seriesCode}     ${month}/${year}` : ` ${month}/${year}`;
};

export const conditionsTooltipFormatter = (
  order: MaturityOrder,
  flexMaturityOptions?: MaturityOrder[]
): string => {
  if (!order) {
    return null;
  }

  const splitOrder = order as MaturityOrder & {
    hasSplit?: boolean | string;
    isSplitMaturity?: boolean | string;
    splitConditionsVarious?: boolean | string;
  };
  const isParent =
    isTrueFlag(order.IS_SPLIT_MATURITY) ||
    isTrueFlag(order.HAS_SPLIT) ||
    isTrueFlag(splitOrder.isSplitMaturity) ||
    isTrueFlag(splitOrder.hasSplit);

  if (isParent) {
    return null;
  }

  const tooltipStrings = [];
  if (order.MUNI_STRIP) {
    tooltipStrings.push(`Strip Order`);
  }
  if (order.MUNI_AON) {
    tooltipStrings.push(`AON`);
  }
  if (order.FLEX_MATURITY) {
    const display = formatFlexMaturityForDisplay(order.FLEX_MATURITY, flexMaturityOptions).trim();
    tooltipStrings.push(display ? `Flex Maturity: ${display}` : `Flex Maturity`);
  }
  if (order.FLEX_COUPON) {
    tooltipStrings.push(`Flex Coupon: ${order.FLEX_COUPON}`);
  }
  const priorityTypeLabel = formatPriorityTypeForDisplay(order.PRIORITY_TYPE);
  if (priorityTypeLabel) {
    tooltipStrings.push(`Priority Type: ${priorityTypeLabel}`);
  }
  if (parseFloat(order.MINIMUM_ORDER_AMOUNT)) {
    tooltipStrings.push(`Minimum Order Amount ${orderAmountFormatter(order.MINIMUM_ORDER_AMOUNT)}`);
  }
  if (parseFloat(order.MAXIMUM_ORDER_AMOUNT)) {
    tooltipStrings.push(`Maximum Order Amount ${orderAmountFormatter(order.MAXIMUM_ORDER_AMOUNT)}`);
  }
  if (shouldShowOrderTypeInSummary(order.ORDER_TYPE)) {
    tooltipStrings.push(formatOrderTypeForDisplay(order.ORDER_TYPE));
  }
  if (order.ZIP_CODE) {
    tooltipStrings.push(`Zip: ${order.ZIP_CODE}`);
  }
  if (order.COMMENT) {
    tooltipStrings.push(`Comment: ${order.COMMENT}`);
  }

  return tooltipStrings.join(', ');
};

export const getTotalsRowData = (orders: MaturityOrder[], isSingleSeries: boolean = false) => {
  const totalQuantity = orders.reduce((acc, o) => acc + (parseFloat(o.QUANTITY) || 0), 0);

  return {
    MATURITY_DATE: 'Totals',
    MATURITY_AMOUNT: orders.reduce((acc, o) => acc + (parseFloat(o.MATURITY_AMOUNT) || 0), 0),
    COUPON: '',
    YIELD: '',
    PRICE: '',
    CALL_FEATURE_DESCRIPTION: '',
    QUANTITY: totalQuantity,
    allConditions: isSingleSeries ? 'Total Orders' : 'Series Orders / Deal Orders',
    status: '',
    actions: '',
  };
};

/**
 * Normalizes numeric values for storage/submission
 * - Returns null for: null, undefined, empty string, NaN, or zero
 * - Returns string value for valid non-zero numbers
 * Used to distinguish cleared fields (null) from zero values
 * 
 * @param value - The value to normalize
 * @returns String value for valid numbers, null for empty/zero/invalid
 */
export const normalizeNumericValueForStorage = (
  value: string | number | null | undefined,
  order?: MaturityOrder | null,
): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const strValue = String(value).trim();
  if (strValue === '' || strValue === 'NaN') {
    return null;
  }
  const num = parseFloat(strValue.replace(/,/g, ''));
  if (isNaN(num)) {
    return null;
  }
  if (num === 0) {
    return canSetZeroCancelOrderAmount(order) ? '0' : null;
  }
  // Order amounts are always integers; normalize to integer string (no float)
  return String(Math.floor(num));
};

/**
 * Normalizes numeric values for display in input fields
 * - Returns empty string for: null, undefined, empty string, NaN, or zero
 * - Returns string value for valid non-zero numbers
 * Used to show cleared fields as empty inputs
 * 
 * @param value - The value to normalize
 * @returns String value for valid numbers, empty string for null/zero/invalid
 */
export const normalizeNumericValueForDisplay = (
  value: string | number | null | undefined,
  order?: MaturityOrder | null,
): string => {
  const normalized = normalizeNumericValueForStorage(value, order);
  return normalized ?? '';
};

/** Event MuniOrderData.isSplit is Boolean? — send true for split children only; omit otherwise (not false). */
export const isSplitOrderForApi = (
  order: Pick<MaturityOrder, 'IS_SPLIT' | 'SPLIT_LINE_NO' | 'LINE_NO'>,
): boolean =>
  order.IS_SPLIT === true ||
  (order.LINE_NO != null && order.LINE_NO > 0) ||
  (order.SPLIT_LINE_NO != null && order.SPLIT_LINE_NO > 0);

/**
 * Maps order data for submission to backend
 * Normalizes numeric fields: cleared fields → null, valid values → string
 */
export const mapMaturityOrderForSubmission = (
  order: MaturityOrder,
  options?: { isClearingAllAmounts?: boolean },
) => {
  const dummyAccountCode = 'TESTACTCODE';
  const isClearingAllAmounts = options?.isClearingAllAmounts === true;
  return {
    ORDER_ID: order.ORDER_ID || '',
    SERIES_ID: Number(order.SERIES_ID),
    ACCOUNT_CODE: dummyAccountCode,
    ORDER_TYPE: normalizeOrderTypeForApi(order.ORDER_TYPE),
    RETAIL_TYPE: resolveRetailTypeForSubmission(order),
    MATURITY_ID: Number(order.MATURITY_ID),
    ORDER_STATUS: order.ORDER_STATUS || OrderStatusEnum.DRAFT,
    MINIMUM_ORDER_AMOUNT: normalizeNumericValueForStorage(order.MINIMUM_ORDER_AMOUNT),
    MAXIMUM_ORDER_AMOUNT: normalizeNumericValueForStorage(order.MAXIMUM_ORDER_AMOUNT),
    MUNI_AON: order.MUNI_AON,
    MUNI_STRIP: order.MUNI_STRIP,
    PRIORITY_TYPE: resolvePriorityTypeForSubmission(order),
    ZIP_CODE: order.ZIP_CODE ?? '',
    COMMENT: order.COMMENT ?? '',
    FLEX_MATURITY: order.FLEX_MATURITY || null,
    FLEX_COUPON: order.FLEX_COUPON || null,
    QUANTITY: isClearingAllAmounts ? null : normalizeNumericValueForStorage(order.QUANTITY, order),
    IS_AUTO_RESET_ORDER_AMOUNT: order.IS_AUTO_RESET_ORDER_AMOUNT || false,
    IS_ZERO_CANCEL_INTENT: order.IS_ZERO_CANCEL_INTENT === true,
    IS_CLEARING_ALL_AMOUNTS: isClearingAllAmounts,
    ...(isSplitOrderForApi(order) ? { IS_SPLIT: true } : {}),
    ...(order.SPLIT_LINE_NO != null ? { SPLIT_LINE_NO: order.SPLIT_LINE_NO } : {}),
  };
};

export const mapMaturityOrderForDeletion = (order: MaturityOrder) => {
  return {
    ORDER_ID: order.ORDER_ID || '',
    SERIES_ID: Number(order.SERIES_ID),
    ACCOUNT_CODE: 'TESTACTCODE',
    ORDER_TYPE: normalizeOrderTypeForApi(order.ORDER_TYPE),
    MATURITY_ID: Number(order.MATURITY_ID),
    MINIMUM_ORDER_AMOUNT: null,
    MAXIMUM_ORDER_AMOUNT: null,
    QUANTITY: null,
  };
};

export const allowOnlyWholePositiveNumbers = (value: string): string => {
  // First, truncate everything after a decimal point (ignore decimal portion)
  const beforeDecimal = value.split('.')[0];
  // Then remove all non-digit characters
  return beforeDecimal.replace(/[^0-9]/g, '');
};

/**
 * Compares two values after normalizing to digits-only.
 * Returns true if the normalized values differ.
 */
export const hasNumericValueChanged = (
  newValue: string | number | null | undefined,
  previousValue: string | number | null | undefined
): boolean => {
  const normalizedNew = allowOnlyWholePositiveNumbers(String(newValue ?? ''));
  const normalizedPrev = allowOnlyWholePositiveNumbers(String(previousValue ?? ''));
  return normalizedNew !== normalizedPrev;
};

/**
 * Calculates the new cursor position after sanitizing input.
 * Adjusts for characters removed before the cursor position.
 */
export const calculateCaretPositionAfterSanitization = (
  originalValue: string,
  cursorPosition: number
): number => {
  const originalBeforeCursor = originalValue.slice(0, cursorPosition);
  const sanitizedBeforeCursor = allowOnlyWholePositiveNumbers(originalBeforeCursor);
  const sanitizedTotal = allowOnlyWholePositiveNumbers(originalValue);

  const removedBeforeCursor = originalBeforeCursor.length - sanitizedBeforeCursor.length;
  const tentativePosition = cursorPosition - removedBeforeCursor;

  // Clamp between 0 and the length of the sanitized value
  return Math.max(0, Math.min(sanitizedTotal.length, tentativePosition));
};

export const formatCallDateOverride = (callDateOverride: string): string => {
  if (!callDateOverride) return callDateOverride;

  // Check if the date is in YYYY-MM-DD format
  const isoDateMatch = callDateOverride.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    // Convert YYYY-MM-DD to MM/DD/YYYY, preserving everything else in the string
    return callDateOverride.replace(/(\d{4})-(\d{2})-(\d{2})/, '$2/$3/$1');
  }

  // Convert MM-DD-YYYY to MM/DD/YYYY (idempotent - safe if already has slashes)
  return callDateOverride.replace(/(\d{2})-(\d{2})-(\d{4})/, '$1/$2/$3');
};

const hasPositiveOrderAmount = (value: unknown): boolean => {
  const parsed = parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) && parsed > 0;
};

const ORDER_CONDITION_VALUE_FIELDS: (keyof MaturityOrder)[] = [
  'ORDER_TYPE',
  'RETAIL_TYPE',
  'PRIORITY_TYPE',
  'MUNI_AON',
  'MUNI_STRIP',
  'MINIMUM_ORDER_AMOUNT',
  'MAXIMUM_ORDER_AMOUNT',
  'COMMENT',
  'ZIP_CODE',
  'FLEX_MATURITY',
  'FLEX_COUPON',
  'ORDER_AMOUNT_INCREMENT',
];

/** True when any order condition field value differs between two orders. */
export const haveOrderConditionValuesChanged = (
  previousOrder: MaturityOrder,
  updatedOrder: MaturityOrder,
): boolean =>
  ORDER_CONDITION_VALUE_FIELDS.some((field) => previousOrder[field] !== updatedOrder[field]);

const resolvePriorityTypeFingerprintValue = (priorityType?: string): string =>
  priorityType && priorityType !== PriorityTypeEnum.NONE ? priorityType : '';

/** Which condition types are active — values ignored except PRIORITY_TYPE; used for split parent "Various" rollup. */
export const getSplitConditionsSelectionFingerprint = (order: MaturityOrder): string => {
  const orderType = normalizeOrderTypeForApi(order.ORDER_TYPE);
  return [
    orderType !== MuniOrderType.OTHER,
    hasRetailTypeValue(order.RETAIL_TYPE),
    resolvePriorityTypeFingerprintValue(order.PRIORITY_TYPE),
    order.MUNI_AON === true,
    order.MUNI_STRIP === true,
    hasPositiveOrderAmount(order.MINIMUM_ORDER_AMOUNT),
    hasPositiveOrderAmount(order.MAXIMUM_ORDER_AMOUNT),
    !!String(order.COMMENT ?? '').trim(),
    !!String(order.ZIP_CODE ?? '').trim(),
    !!String(order.FLEX_MATURITY ?? '').trim(),
    !!String(order.FLEX_COUPON ?? '').trim(),
    hasPositiveOrderAmount(order.ORDER_AMOUNT_INCREMENT),
  ].join('|');
};

export const hasOrderConditions = (order: MaturityOrder): boolean => {
  if (!order) return false;

  return !!(
    order.MUNI_STRIP ||
    order.MUNI_AON ||
    hasPositiveOrderAmount(order.MINIMUM_ORDER_AMOUNT) ||
    hasPositiveOrderAmount(order.MAXIMUM_ORDER_AMOUNT) ||
    order.COMMENT ||
    order.ZIP_CODE ||
    (order.PRIORITY_TYPE && order.PRIORITY_TYPE !== PriorityTypeEnum.NONE) ||
    order.FLEX_MATURITY ||
    order.FLEX_COUPON ||
    hasRetailTypeValue(order.RETAIL_TYPE)
  );
};

export const getClearAllMessages = (
  draftOrders: MaturityOrder[]
): {
  clearAllModalHeader: string;
  clearAllModalBody: string;
  clearAllModalBodySecondLine: string;
  clearAllModalButtonText: string;
} => {
  const hasDraft = draftOrders.some(
    (order) => order.ORDER_STATUS === OrderStatusEnum.DRAFT,
  );
  const hasSubmittedAndDraft = draftOrders.some(
    (order) => order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT,
  );

  // Scenario 3: Both DRAFT and SUBMITTED_AND_DRAFT
  if (hasDraft && hasSubmittedAndDraft) {
    return {
      clearAllModalHeader: 'Clear or Reset all draft Orders',
      clearAllModalBody: 'Are you sure you want to clear or reset all draft orders?',
      clearAllModalBodySecondLine: 'Orders In Transit will not be cleared or reset.',
      clearAllModalButtonText: 'Yes, Clear or Reset',
    };
  }

  // Scenario 2: Only SUBMITTED_AND_DRAFT
  if (hasSubmittedAndDraft) {
    return {
      clearAllModalHeader: 'Reset all draft Orders',
      clearAllModalBody: 'Are you sure you want to reset all draft orders?',
      clearAllModalBodySecondLine: 'Orders In Transit will not be reset.',
      clearAllModalButtonText: 'Yes, Reset',
    };
  }

  // Scenario 1: Only DRAFT
  return {
    clearAllModalHeader: 'Clear all draft Orders',
    clearAllModalBody: 'Are you sure you want to clear all draft orders?',
    clearAllModalBodySecondLine: 'Orders In Transit will not be cleared.',
    clearAllModalButtonText: 'Yes, Clear',
  };
};

/**
 * Core validation function for amounts against multiples requirements:
 * - Returns valid if amount is empty, null, undefined, or zero (no amount set)
 * - Must be >= multiples (or >= 5 if multiples is null)
 * - Must be evenly divisible by multiples (or by 5 if multiples is null)
 * - When multiples is null and par is not divisible by 5: minimum may be any positive value;
 *   quantity and maximum use minimum as increment when minimum is set, otherwise no divisibility rule.
 */
export const validateOrderAmountForMultiples = (
  amount: string | null | undefined,
  multiples?: number,
  context?: OrderAmountMultiplesContext
): { isValid: boolean; errorMessage?: string } => {
  const amountStr = amount == null ? "" : String(amount).trim();

  if (amountStr === "" || parseFloat(amountStr) <= 0) {
    return { isValid: true };
  }
  
  const amountNum = parseFloat(amountStr);

  if (isMinimumIncrementMode(multiples, context?.maturityAmount)) {
    if (context?.field === 'minimum') {
      return { isValid: true };
    }
    const minNum = context?.minimumOrderAmount ? parseFloat(String(context.minimumOrderAmount)) : 0;
    if (minNum > 0) {
      if (amountNum < minNum) {
        return {
          isValid: false,
          errorMessage: `Must be greater than ${minNum.toLocaleString()}`,
        };
      }
      if (amountNum % minNum !== 0) {
        return {
          isValid: false,
          errorMessage: `Must be evenly divisible by ${minNum.toLocaleString()}`,
        };
      }
    }
    return { isValid: true };
  }

  const effectiveMultiples = (!multiples || multiples <= 0) ? DEFAULT_NULL_MULTIPLES : multiples;

  // Check if amount is less than multiples
  if (amountNum < effectiveMultiples) {
    return {
      isValid: false,
      errorMessage: effectiveMultiples === 5 
        ? 'Must be greater than 5'
        : `Must be greater than ${effectiveMultiples.toLocaleString()}`,
    };
  }

  // Check if amount is evenly divisible by multiples
  if (amountNum % effectiveMultiples !== 0) {
    return {
      isValid: false,
      errorMessage: effectiveMultiples === 5
        ? 'Must be evenly divisible by 5'
        : `Must be evenly divisible by ${effectiveMultiples.toLocaleString()}`,
    };
  }

  return { isValid: true };
};

export type { QuantityCellValidationState } from './deal-series.types';

export const getBaseQuantityCellValidationState = (order: MaturityOrder): QuantityCellValidationState => {
  if (order.IS_ZERO_CANCEL_INTENT) {
    return { hasError: false, hasWarning: false, tooltip: null };
  }
  const quantity = parseFloat(order?.QUANTITY);
  if (!quantity) {
    return { hasError: false, hasWarning: false, tooltip: null };
  }
  const minimumRequiredValidation = validateMinimumRequiredForMinimumIncrementMode(
    order.MULTIPLES,
    order.MATURITY_AMOUNT,
    order.MINIMUM_ORDER_AMOUNT,
    order.QUANTITY
  );
  if (minimumRequiredValidation.hasError) {
    return {
      hasError: true,
      hasWarning: false,
      tooltip: minimumRequiredValidation.message,
    };
  }
  const orderCap = getOrderAmountCap(order);
  const exceedsMaturity = !hasValidOrderQuantity(order.QUANTITY, orderCap);
  const multiplesValidation = validateOrderAmountForMultiples(order.QUANTITY, order.MULTIPLES, {
    maturityAmount: order.MATURITY_AMOUNT,
    minimumOrderAmount: order.MINIMUM_ORDER_AMOUNT,
    field: 'quantity',
  });
  const hasMultiplesError = !multiplesValidation.isValid;
  if (exceedsMaturity) {
    if (isSplitMaturityParentRow(order)) {
      return {
        hasError: false,
        hasWarning: true,
        tooltip: SPLIT_MATURITY_PAR_EXCEEDED_TOOLTIP,
      };
    }
    return {
      hasError: true,
      hasWarning: false,
      tooltip: getOrderAmountCapExceededTooltip(order, order.QUANTITY, orderCap),
    };
  }
  if (hasMultiplesError) {
    return {
      hasError: true,
      hasWarning: false,
      tooltip: multiplesValidation.errorMessage ?? '',
    };
  }
  const hasWarning = breachesOrderConditions(
    order.QUANTITY,
    order.MINIMUM_ORDER_AMOUNT,
    order.MAXIMUM_ORDER_AMOUNT
  );
  if (hasWarning) {
    const minNum = parseFloat(order.MINIMUM_ORDER_AMOUNT) || 0;
    const maxNum = parseFloat(order.MAXIMUM_ORDER_AMOUNT) || 0;
    let tooltip: string | null = null;
    if (minNum > 0 && quantity < minNum) {
      tooltip = `Order Amount (${orderAmountFormatter(order.QUANTITY)}) less than the Minimum Condition (${orderAmountFormatter(order.MINIMUM_ORDER_AMOUNT)})`;
    } else if (maxNum > 0 && quantity > maxNum) {
      tooltip = `Order Amount (${orderAmountFormatter(order.QUANTITY)}) exceeds the Maximum Condition (${orderAmountFormatter(order.MAXIMUM_ORDER_AMOUNT)})`;
    }
    return { hasError: false, hasWarning: true, tooltip };
  }
  return { hasError: false, hasWarning: false, tooltip: null };
};



/**
 * Returns true when order is in draft state (DRAFT or SUBMITTED_AND_DRAFT).
 */
export const isDraftOrder = (order: MaturityOrder): boolean =>
  order?.ORDER_STATUS === OrderStatusEnum.DRAFT ||
  order?.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT;

/**
 * Returns true when a draft order with no conditions/quantity should be deleted after clearing.
 */
export const shouldDeleteDraftOrder = (order: MaturityOrder, previousOrder?: MaturityOrder): boolean => {
  if (!order) return false;
  const quantity = parseFloat(order.QUANTITY);
  const isDraft = order.ORDER_STATUS === OrderStatusEnum.DRAFT;
  const hasNoQuantity = !quantity || quantity === 0;
  const hasNoConditions = !hasOrderConditions(order);
  const previousQuantity = previousOrder ? parseFloat(previousOrder.QUANTITY) : 0;
  const quantityWasDeleted = previousQuantity > 0 && hasNoQuantity;

  return isDraft && hasNoQuantity && hasNoConditions && !!order.ORDER_ID && quantityWasDeleted;
};

/** True when the grid's rendered bottom fits within the viewport — no internal scroll needed. */
export const gridFitsViewport = (naturalBottomPx: number, viewportBottomPx: number): boolean =>
  naturalBottomPx <= viewportBottomPx;

/** Capped grid container height when the grid would extend past the viewport bottom. */
export const cappedGridHeightPx = (gridTopPx: number, viewportBottomPx: number): number =>
  Math.max(viewportBottomPx - gridTopPx, 0);

/** Matches deal-series gridOptions headerHeight / rowHeight. */
export const GRID_HEADER_HEIGHT_PX = 40;
export const GRID_ROW_HEIGHT_PX = 40;
export const GRID_PINNED_ROW_HEIGHT_PX = 40;
/** Horizontal scrollbar + borders buffer for overflow estimate. */
export const GRID_SCROLLBAR_BUFFER_PX = 17;

export const estimateGridContentHeightPx = (dataRowCount: number): number =>
  GRID_HEADER_HEIGHT_PX +
  dataRowCount * GRID_ROW_HEIGHT_PX +
  GRID_PINNED_ROW_HEIGHT_PX +
  GRID_SCROLLBAR_BUFFER_PX;

/** Proactive overflow check — cap before autoHeight so the page does not grow. */
export const gridWouldOverflowViewport = (
  gridTopPx: number,
  viewportBottomPx: number,
  dataRowCount: number,
): boolean =>
  estimateGridContentHeightPx(dataRowCount) > cappedGridHeightPx(gridTopPx, viewportBottomPx);

/** Cap boundary is the deals route bottom so open-deal tabs above stay fixed. */
export const getGridViewportBottomPx = (gridContainer: HTMLElement): number => {
  const route = gridContainer.closest('deals-route');
  return route?.getBoundingClientRect().bottom ?? window.innerHeight;
};
